import { Dispatch, SetStateAction, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Card, CardContent, FormHelperText, Grid, InputAdornment } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { selectUserInformation } from "../../State/Slices/UserInformationSlice";
import LoadingBar from "../LoadingBar/LoadingBar";
import { StyledTextField } from "../TextField/TextField";
import {
  ClientBillingTier,
  CreateClientBillingTier,
  UpdateClientBillingTier,
} from "../../Types/ClientBilling";
import { createClientBillingTier } from "../../Services/API/ClientBillingsTier";
import {
  updateBillingTiersData,
  useClientBillingTiers,
} from "../../Helpers/billingTierDefaultPricing";

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    justifyContent: "space-between",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  error: {
    color: theme.palette.error.main,
  },
  textField: {
    "& .Mui-error": {
      color: theme.palette.error.main,
    },
    "& .MuiFormHelperText-root": {
      color: theme.palette.error.main,
    },
    "& .MuiInput-underline.Mui-error:after": {
      borderBottomColor: theme.palette.error.main,
    },
  },
}));

interface CreateBillingTierModalProps {
  openModal: Dispatch<SetStateAction<boolean>>;
  setShowError: Dispatch<SetStateAction<boolean>>;
  setShowSignifier: Dispatch<SetStateAction<boolean>>;
  setCreateTierErrorMessage: Dispatch<SetStateAction<string>>;
}

export function validateCreateBillingTierData(
  data: CreateClientBillingTier | UpdateClientBillingTier,
  tiers: ClientBillingTier[],
  isUpdate?: boolean
) {
  return (
    !!data.userId &&
    !!data.tierName &&
    (isUpdate || !tiers.some((t) => t.tierName.toLowerCase() === data.tierName.toLowerCase())) &&
    data.monthlyBasePrice >= 0 &&
    data.candidateQuota >= 0 &&
    data.emailQuota >= 0 &&
    data.emailRate >= 0 &&
    data.textQuota >= 0 &&
    data.textRate >= 0
  );
}

export default function CreateBillingTierModal({
  openModal,
  setShowSignifier,
  setShowError,
  setCreateTierErrorMessage,
}: CreateBillingTierModalProps) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const { "http://nasrecruitment.io/activateId": userId } = useSelector(selectUserInformation);
  const { tiers } = useClientBillingTiers();
  const [showInputError, setShowInputError] = useState(false);
  const [name, setName] = useState("");
  const [basePrice, setBasePrice] = useState(0);
  const [candidateAllowance, setCandidateAllowance] = useState(0);
  const [pricePerCandidate, setPricePerCandidate] = useState(0);
  const [emailAllowance, setEmailAllowance] = useState(0);
  const [pricePerEmail, setPricePerEmail] = useState(0);
  const [textAllowance, setTextAllowance] = useState(0);
  const [pricePerText, setPricePerText] = useState(0);

  const [addProcessing, setAddProcessing] = useState(false);

  const handleInputTierName = (tierName: string) => {
    setName((_) => tierName);

    const currentBillingTierData = {
      userId,
      tierName,
      monthlyBasePrice: basePrice,
      candidateQuota: candidateAllowance,
      emailQuota: emailAllowance,
      emailRate: pricePerEmail,
      textQuota: textAllowance,
      textRate: pricePerText,
    } as CreateClientBillingTier;

    if (!validateCreateBillingTierData(currentBillingTierData, tiers)) {
      setShowInputError(true);
    } else {
      setShowInputError(false);
    }
  };

  const submitData = async () => {
    const currentBillingTierData = {
      userId,
      tierName: name,
      monthlyBasePrice: basePrice,
      candidateQuota: candidateAllowance,
      emailQuota: emailAllowance,
      emailRate: pricePerEmail,
      textQuota: textAllowance,
      textRate: pricePerText,
    } as CreateClientBillingTier;

    if (!showInputError && validateCreateBillingTierData(currentBillingTierData, tiers)) {
      setAddProcessing(true);
      try {
        await createClientBillingTier(currentBillingTierData);
        setShowSignifier(true);
        updateBillingTiersData(dispatch);
        openModal(false);
      } catch (e: any) {
        setCreateTierErrorMessage(e.message);
        setShowError(true);
        setShowSignifier(true);
      }
      setAddProcessing(false);
    } else {
      setShowInputError(true);
    }
  };

  return (
    <div className={classes.root}>
      <LoadingBar loading={addProcessing} />
      <Grid>
        <Card>
          <CardContent>
            <Grid container spacing={1}>
              <Grid xs={12} sm={12} item>
                <p>Add Tier</p>
              </Grid>
              <Grid item xs={12} sm={12}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  label="Name"
                  value={name}
                  error={showInputError && name === ""}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  onChange={(e) => handleInputTierName(e.target.value)}
                />
                {showInputError && name === "" && (
                  <FormHelperText className={classes.error}>Name Required</FormHelperText>
                )}
                {showInputError &&
                  tiers.some((t) => t.tierName.toLowerCase() === name.toLowerCase()) && (
                    <FormHelperText className={classes.error}>
                      Tier named "{name}" already exists
                    </FormHelperText>
                  )}
              </Grid>
              <Grid item xs={12} sm={12}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  type="number"
                  label="Base Price"
                  value={basePrice}
                  error={showInputError && basePrice < 0}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 0.01 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  onChange={(e) => setBasePrice(Number(e.target.value))}
                />
                {showInputError && basePrice < 0 && (
                  <FormHelperText className={classes.error}>Base Price Required</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  type="number"
                  label="Candidate Allowance"
                  value={candidateAllowance}
                  error={showInputError && candidateAllowance < 0}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                  onChange={(e) => setCandidateAllowance(Number(e.target.value))}
                />
                {showInputError && candidateAllowance < 0 && (
                  <FormHelperText className={classes.error}>
                    Candidate Allowance Required
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  type="number"
                  label="Price Per Overage Candidate"
                  value={pricePerCandidate}
                  error={showInputError && pricePerCandidate < 0}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  onChange={(e) => setPricePerCandidate(Number(e.target.value))}
                />
                {showInputError && pricePerCandidate < 0 && (
                  <FormHelperText className={classes.error}>
                    Price Per Overage Candidate Required
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  type="number"
                  label="Email Allowance"
                  value={emailAllowance}
                  error={showInputError && emailAllowance < 0}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                  onChange={(e) => setEmailAllowance(Number(e.target.value))}
                />
                {showInputError && emailAllowance < 0 && (
                  <FormHelperText className={classes.error}>
                    Email Allowance Required
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  type="number"
                  label="Price Per Overage Email"
                  value={pricePerEmail}
                  error={showInputError && pricePerEmail < 0}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  onChange={(e) => setPricePerEmail(Number(e.target.value))}
                />
                {showInputError && pricePerEmail < 0 && (
                  <FormHelperText className={classes.error}>
                    Price Per Overage Email Required
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  type="number"
                  label="Text Message Allowance"
                  value={textAllowance}
                  error={showInputError && textAllowance < 0}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  inputProps={{ min: 0, step: 1 }}
                  onChange={(e) => setTextAllowance(Number(e.target.value))}
                />
                {showInputError && textAllowance < 0 && (
                  <FormHelperText className={classes.error}>Text Message Required</FormHelperText>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledTextField
                  InputLabelProps={{ style: { color: "rgba(0,0,0,.6)" } }}
                  type="number"
                  label="Price Per Overage Text Message"
                  value={pricePerText}
                  error={showInputError && pricePerText < 0}
                  className={classes.textField}
                  variant="standard"
                  fullWidth
                  required
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  onChange={(e) => setPricePerText(Number(e.target.value))}
                />
                {showInputError && pricePerText < 0 && (
                  <FormHelperText className={classes.error}>
                    Price Per Overage Text Message Required
                  </FormHelperText>
                )}
              </Grid>
              <Grid
                container
                justifyContent="flex-end"
                direction="row"
                style={{ marginRight: "10px" }}
              >
                <Grid item xs={6} sm={3}>
                  <Button
                    style={{ color: "#82B288", marginTop: "20px" }}
                    type="submit"
                    variant="outlined"
                    fullWidth
                    onClick={() => openModal(false)}
                  >
                    Cancel
                  </Button>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Button
                    style={{ marginTop: "20px", marginLeft: "10px" }}
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={submitData}
                    disabled={addProcessing || showInputError}
                  >
                    Save
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </div>
  );
}
